import {FilterIds} from '@/types/models/creator-crm/filterId';
import SortField from '@/types/models/creator-crm/sortField';

/**
 * Filter and sort field IDs that are hidden when search mode is enabled.
 * Search mode is for searching creators, while CRM mode has additional filtering options.
 */
const HIDDEN_IN_SEARCH_MODE = new Set<string>(['vettingDecision']);

/**
 * Determines if an item (filter or sort field) should be displayed in the current mode.
 * @param id The ID of the filter or sort field
 * @param isSearchMode Whether search mode is enabled
 * @returns true if the item should be displayed, false otherwise
 */
function isVisibleInMode(
  id: FilterIds | string,
  isSearchMode: boolean | undefined
): boolean {
  if (!isSearchMode) {
    return true;
  }
  return !HIDDEN_IN_SEARCH_MODE.has(id);
}

/**
 * Determines if a filter should be displayed based on the current mode.
 * @param filterId The ID of the filter
 * @param isSearchMode Whether search mode is enabled
 * @returns true if the filter should be displayed, false otherwise
 */
export function shouldShowFilter(
  filterId: FilterIds,
  isSearchMode: boolean | undefined
): boolean {
  return isVisibleInMode(filterId, isSearchMode);
}

/**
 * Determines if a sort field should be displayed in the current mode.
 * @param sortField The sort field
 * @param isSearchMode Whether search mode is enabled
 * @returns true if the sort field should be displayed, false otherwise
 */
export function shouldShowSortField(
  sortField: SortField | string,
  isSearchMode: boolean | undefined
): boolean {
  return isVisibleInMode(sortField, isSearchMode);
}

/**
 * Determines if a table column should be displayed in the current mode.
 * @param columnId The column ID
 * @param isSearchMode Whether search mode is enabled
 * @returns true if the column should be displayed, false otherwise
 */
export function shouldShowColumn(
  columnId: string,
  isSearchMode: boolean | undefined
): boolean {
  return isVisibleInMode(columnId, isSearchMode);
}

export default shouldShowFilter;
